import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {firstValueFrom} from 'rxjs';
import {environment} from '@/environments/environment';
import { apiBaseUrl } from './api-endpoints';

export interface RuntimeConfig {
    apiBaseUrl?: string;
    authBaseUrl?: string;
}

@Injectable({providedIn: 'root'})
export class RuntimeConfigService {
    private config: Required<RuntimeConfig> = {
        apiBaseUrl: '',
        authBaseUrl: '',
    };

    constructor(private http: HttpClient) {
    }

    /**
     * Se ejecuta vía APP_INITIALIZER.
     * Carga src/assets/runtime-config.json y aplica fallback a environment si falta o viene vacío.
     */
    async load(): Promise<void> {
        try {
            const cfg = await firstValueFrom(
                this.http.get<RuntimeConfig>('assets/runtime-config.json', {responseType: 'json' as const})
            );

            this.config.apiBaseUrl = this.normalizeBaseUrl(cfg?.apiBaseUrl || '');
            this.config.authBaseUrl = this.normalizeBaseUrl(cfg?.authBaseUrl || '');
        } catch (err) {
            // Si no existe el archivo o falla la carga, seguimos con fallback
            // (no lanzamos error para no romper el bootstrap)
        }

        // Fallbacks: si runtime-config viene vacío, usamos environment
        if (!this.config.apiBaseUrl) {
            this.config.apiBaseUrl = this.normalizeBaseUrl(environment.url || '');
        }
        if (!this.config.authBaseUrl) {
            this.config.authBaseUrl = this.normalizeBaseUrl(apiBaseUrl('auth'));
        }

        // Validación mínima (opcional, pero útil)
        // Si igual quedan vacíos, al menos que reviente con un mensaje claro.
        if (!this.config.apiBaseUrl || !this.config.authBaseUrl) {
            console.warn('[RuntimeConfig] apiBaseUrl/authBaseUrl están vacíos. Revisa runtime-config.json o environment.');
        }
    }

    /** Base URL ERP, ej: https://backend.getmarket.cl/v1/erp */
    get apiBaseUrl(): string {
        return this.config.apiBaseUrl;
    }

    /** Base URL Auth, ej: https://auth.getmarket.cl/v1/auth */
    get authBaseUrl(): string {
        return this.config.authBaseUrl;
    }

    /**
     * Construye URL final: base + /path (sin dobles //)
     * Ej: apiUrl('/products') -> https://.../v1/erp/products
     */
    apiUrl(path: string): string {
        return this.join(this.apiBaseUrl, path);
    }

    authUrl(path: string): string {
        return this.join(this.authBaseUrl, path);
    }

    private normalizeBaseUrl(url: string): string {
        const u = (url || '').trim();
        if (!u) return '';
        // Quita slash final
        return u.endsWith('/') ? u.slice(0, -1) : u;
    }

    private join(base: string, path: string): string {
        const b = this.normalizeBaseUrl(base);
        const p = (path || '').trim();

        if (!p) return b;
        if (p.startsWith('http://') || p.startsWith('https://')) return p;

        const cleanPath = p.startsWith('/') ? p.slice(1) : p;
        return `${b}/${cleanPath}`;
    }
}
